import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CheckIcon } from "components/Icons/check.svg";
import { ReactComponent as DotsVerticalIcon } from "components/Icons/dots-vertical.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import dayjs from "dayjs";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { useClickDifferentiation } from "hooks/useClickRecognition";
import { kebabCase } from "lodash";
import { memo, useMemo, useRef, useState } from "react";
import { EffectDateTimeDialog } from "./EffectDateTimeDialog";
import { EffectDialog } from "./EffectDialog";
import { EffectItemsCriteriaDialog } from "./EffectItemsCriteriaDialog";
import { DataItemType } from "./EffectSearch";
import EffectWalletDialog from "./EffectWalletDialog";
import { getCriterionIntialValues } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    chips: {
      border: `1px solid transparent`,
      background: theme.primary500,
      color: theme.white50,
      borderRadius: "4px",
      margin: theme.spacing(2, 0),
      fontSize: "14px",
      display: "inline-flex",
      whiteSpace: "nowrap",
      userSelect: "none",
      transition: "0.25s",
      maxWidth: "calc(100% - 38px)",
      alignItems: "center",
    },
    code: {
      position: "relative",
      padding: theme.spacing(1.25, 5),
      overflow: ({ isEditingExpression }: { isEditingExpression: boolean }) =>
        isEditingExpression ? "auto" : "hidden",
      whiteSpace: "nowrap",
      textOverflow: ({
        isEditingExpression,
      }: {
        isEditingExpression: boolean;
      }) => (isEditingExpression ? "unset" : "ellipsis"),

      "&:focus-visible": {
        outline: "none",
      },
    },
    menu: {
      display: "flex",
      alignItems: "center",
      borderLeft: `1px solid ${theme.black}`,
      padding: theme.spacing(2),
      cursor: "pointer",
      userSelect: "none",

      "&:hover": {
        background: theme.primary600,
        fill: theme.white00,
        color: theme.white00,
      },
    },
    editing: {
      border: `1px solid ${theme.primary500}`,
      background: theme.white00,
      color: theme.gray500,
      zIndex: 10002,
    },
    backdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      background: "transparent",
      opacity: 0.2,
      width: "100vw",
      height: "100vh",
      zIndex: 10001,
    },
  }),
);

interface Props {
  code: string;
  items: DataItemType[];
  setIsEdittingChips: (val: boolean) => void;
  saveValue: () => void;
  valueRef: HTMLSpanElement | null;
  setOpen: (val: boolean) => void;
}

type Dialog = {
  isOpen: boolean;
  type?: "text" | "datetime" | "itemsCriteria" | "wallet";
  title: string;
  description?: React.ReactElement | string;
  code: string;
  parameter?: string;
};

export const getExpressionValue = (
  dialog: Dialog | null,
  expression: string,
) => {
  if (!dialog?.code || !dialog.parameter) return "";

  // For the special case with getLabelValue
  if (
    dialog.code.includes("getLabelValue") &&
    expression.includes("getLabelValue")
  ) {
    const labelMatch = expression.match(/getLabelValue\(['"](.+?)['"]\)/);
    if (labelMatch && labelMatch[1]) {
      return labelMatch[1];
    }

    return "";
  }

  return (
    expression
      .match(/\((.*)\)/)
      ?.pop()
      // Remove 1st and last char: ' or "
      ?.slice(1, -1)
  );
};

export const Chips = memo(
  ({
    code,
    items,
    setIsEdittingChips,
    saveValue,
    valueRef,
    setOpen,
  }: Props) => {
    const [expression, setExpression] = useState(code);
    const [dialog, setDialog] = useState<Dialog | null>({
      isOpen: false,
      type: undefined,
      title: "",
      description: "",
      code: "",
      parameter: undefined,
    });
    const [isEditingExpression, setIsEditingExpression] = useState(false);
    const codeRef = useRef<HTMLSpanElement>(null);
    const expressionRef = useRef<HTMLDivElement>(null);
    const contextMenu = useContextMenu();
    const classes = useStyles({ isEditingExpression });
    const { message } = useMessage();
    const expressionData = items.find(
      (variable) =>
        variable.codeRegexMatcher &&
        code.match(variable.codeRegexMatcher) !== null,
    );
    const isDialogType =
      expressionData &&
      expressionData?.parameter &&
      expressionData.parameterType;

    const singleClick = () => {
      setIsEdittingChips(true);
      setOpen(false);

      if (isDialogType) {
        const dialogOpts = {
          title: expressionData.dialogTitle || expressionData.label,
          description: expressionData.dialogDescription,
          code: expressionData.code as string,
          parameter: expressionData.parameter,
          type: expressionData.parameterType,
          isOpen: true,
        };

        setDialog(dialogOpts);
      }
    };

    const onClick = useClickDifferentiation(singleClick, () => {
      setOpen(false);
      setIsEdittingChips(true);
      setIsEditingExpression(true);
    });

    const currentValue = getExpressionValue(
      dialog,
      codeRef.current?.innerText || expression,
    );
    const initialValuesCriterion = useMemo(
      () =>
        getCriterionIntialValues(
          codeRef.current?.innerText || expression,
          expressionData,
        ),
      // eslint-disable-next-line
      [expression],
    );

    return (
      <div
        className={`${classes.chips} ${
          isEditingExpression ? classes.editing : ""
        }`}
        data-test-id={`effect-search-chips-${kebabCase(expression)}`}
        ref={expressionRef}
      >
        {isEditingExpression && (
          <div
            className={classes.backdrop}
            onClick={() => {
              setIsEditingExpression(false);
              setIsEdittingChips(false);
            }}
            contentEditable={false}
          />
        )}
        <span
          suppressContentEditableWarning={true}
          contentEditable={isEditingExpression}
          onClick={
            isEditingExpression
              ? undefined
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClick();
                }
          }
          className={classes.code}
          style={{
            zIndex: isEditingExpression ? 10002 : 1,
          }}
          autoFocus
          ref={codeRef}
          data-test-id={`expression-${expression}`}
          onKeyDown={(e) => {
            const isEnterPressed = e.key === "Enter";

            if (isEditingExpression && isEnterPressed) {
              setIsEdittingChips(false);
              setIsEditingExpression(false);
              saveValue();
            }
          }}
        >
          {expression}
        </span>

        <span
          contentEditable={false}
          style={{
            zIndex: isEditingExpression ? 10002 : 1,
          }}
          className={classes.menu}
          data-test-id="action-icon"
          onClick={(e) => {
            if (isEditingExpression) {
              setIsEdittingChips(false);
              setIsEditingExpression(false);
              saveValue();
            } else {
              setIsEdittingChips(true);
              contextMenu.handleMenuOpen(e);
            }
          }}
        >
          {isEditingExpression ? <CheckIcon /> : <DotsVerticalIcon />}
          <Menu
            anchorEl={contextMenu.anchor}
            anchorOrigin={{
              horizontal: "left",
              vertical: "bottom",
            }}
            onClose={(e: Event) => {
              e.stopPropagation();
              contextMenu.handleMenuClose();
              setIsEdittingChips(false);
            }}
            open={contextMenu.isMenuOpen}
            transformOrigin={{
              horizontal: "left",
              vertical: "top",
            }}
            variant="menu"
          >
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();

                setIsEdittingChips(false);
                contextMenu.handleMenuClose();

                if (valueRef && expressionRef?.current) {
                  let clonedChips = expressionRef.current?.cloneNode(true);

                  valueRef?.appendChild(clonedChips);

                  saveValue();
                }
              }}
              data-test-id="duplicate-formula"
            >
              {message({ id: "duplicateFormula" })}
            </MenuItem>
            {isDialogType && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();

                  setIsEdittingChips(false);
                  contextMenu.handleMenuClose();

                  return singleClick();
                }}
                data-test-id="edit-formula"
              >
                {message({ id: "editFormula" })}
              </MenuItem>
            )}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();

                contextMenu.handleMenuClose();
                expressionRef.current?.remove();
                saveValue();
              }}
              data-test-id="delete-formula"
            >
              {message({ id: "deleteFormula" })}
            </MenuItem>
          </Menu>
        </span>
        {dialog && dialog.type === "text" && (
          <EffectDialog
            isDialogOpen={dialog.type === "text"}
            closeDialog={() => {
              setIsEdittingChips(false);
              setDialog(null);
            }}
            dialog={dialog}
            setFieldValue={(value) => {
              setExpression(value.replace("<#>", "").replace("</#>", ""));
              saveValue();
            }}
            initialValue={currentValue || ""}
            currentValue=""
            multiInput={false}
          />
        )}
        {dialog && dialog.type === "datetime" && (
          <EffectDateTimeDialog
            isDialogOpen={dialog.type === "datetime"}
            closeDialog={() => {
              setIsEdittingChips(false);
              setDialog(null);
            }}
            dialog={dialog}
            setFieldValue={(value) => {
              setExpression(value.replace("<#>", "").replace("</#>", ""));
              saveValue();
            }}
            initialValue={currentValue ? dayjs(currentValue) : ""}
            currentValue=""
          />
        )}
        {dialog && dialog.type === "itemsCriteria" && (
          <EffectItemsCriteriaDialog
            isDialogOpen={dialog.type === "itemsCriteria"}
            closeDialog={() => {
              setIsEdittingChips(false);
              setDialog(null);
            }}
            dialog={dialog}
            setFieldValue={(value) => {
              setExpression(
                value
                  .replace("<#>", "")
                  .replace("</#>", "")
                  .replaceAll(" ", ""),
              );
              saveValue();
            }}
            initialValues={initialValuesCriterion}
            currentValue={""}
          />
        )}
        {dialog && dialog.type === "wallet" && (
          <EffectWalletDialog
            dialog={dialog}
            isDialogOpen={dialog.type === "wallet"}
            closeDialog={() => {
              setIsEdittingChips(false);
              setDialog(null);
            }}
            setFieldValue={(value) => {
              setExpression(value.replace("<#>", "").replace("</#>", ""));
              saveValue();
            }}
            initialValue={currentValue || ""}
            currentValue=""
            multiInput={false}
          />
        )}
      </div>
    );
  },
);
